import {
  StackLayout,
  Box,
  ParagraphText,
  UtilityText,
  StackItem,
  styled,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { OpensInANewTabMessage } from '@leagueplatform/web-common-components';
import { APP_NAME_LONG, EXTERNAL_URLS } from 'common/constants/constants';
import { ExternalLink } from 'components/external-link/external-link.component';
import { useIsBelowTablet } from 'hooks/use-is-below-tablet/use-is-below-tablet.hooks';
import { useFooterConfig } from 'hooks/use-footer-config/use-footer-config.hook';
import { PAGE_NAVIGATION_WIDTH } from 'common/constants/ui';
import { useFeatureFlags } from 'hooks/use-feature-flags/use-feature-flags.hook';
import { FEATURE_FLAGS } from 'common/firebase';
import { AppStoreLinkProps } from 'common/types/types';

const AppStoreIconImage = styled('img', {
  width: '158px',
});

const AppStoreLink = ({ href, alt, src }: AppStoreLinkProps) => (
  <ExternalLink href={href}>
    <AppStoreIconImage alt={alt} src={src} />
    <Box as="span" css={{ position: 'relative' }}>
      <OpensInANewTabMessage />
    </Box>
  </ExternalLink>
);

export const GlobalFooter = () => {
  const { formatMessage } = useIntl();
  const isBelowTablet = useIsBelowTablet();
  const { footerLinks, footerDescriptionId, planName, appLinks } =
    useFooterConfig();
  const { featureFlags } = useFeatureFlags([FEATURE_FLAGS.APP_STORE]);

  const showAppStoreLinks = featureFlags[FEATURE_FLAGS.APP_STORE];

  return (
    <StackLayout
      horizontalAlignment="center"
      css={{
        backgroundColor: '$interactiveActionPrimary',
        paddingInline: '$three',
        paddingBlock: isBelowTablet ? '$oneAndHalf' : '$three',
        '&& *': {
          color: '$onSurfaceTextReversed',
          '&:focus': {
            borderRadius: '$circle',
            outline: '$borderWidths$thin dotted $colors$onSurfaceTextReversed',
            outlineOffset: '$borderWidths$outerFocus',
          },
        },
      }}
    >
      <StackLayout
        orientation={isBelowTablet ? 'vertical' : 'horizontal'}
        horizontalAlignment="center"
        css={{
          maxWidth: PAGE_NAVIGATION_WIDTH,
        }}
      >
        {showAppStoreLinks && (
          <StackItem
            css={{
              gap: '$threeQuarters',
              marginInlineEnd: isBelowTablet ? '$none' : '$twoAndHalf',
              marginBlockEnd: isBelowTablet ? '$two' : '$none',
            }}
          >
            {appLinks.map((appLink) => (
              <AppStoreLink key={appLink.alt} {...appLink} />
            ))}
          </StackItem>
        )}

        <StackLayout spacing={isBelowTablet ? '$two' : '$oneAndHalf'}>
          <StackLayout
            orientation={isBelowTablet ? 'vertical' : 'horizontal'}
            spacing={isBelowTablet ? '$half' : '$two'}
          >
            {footerLinks.map((footerLink) => (
              <Box
                as="a"
                href={footerLink.to}
                target={footerLink.target}
                rel="noopener noreferrer"
                key={footerLink.text}
                css={{
                  typography: '$subtitleTwo',
                }}
              >
                {footerLink.text}
              </Box>
            ))}
          </StackLayout>

          <Box>
            <ParagraphText
              css={{
                typography: '$caption',
              }}
            >
              {formatMessage(
                {
                  id: footerDescriptionId,
                },
                {
                  fullDisclaimerLink: (
                    <ExternalLink href={EXTERNAL_URLS.DISCLAIMER}>
                      {formatMessage({ id: 'STR_CLICK_READ_FULL_DISCLAIMER' })}
                    </ExternalLink>
                  ),
                  planName,
                },
              )}
            </ParagraphText>
          </Box>

          <UtilityText
            css={{
              typography: '$label',
            }}
          >
            {formatMessage(
              { id: 'STR_SCAN_HEALTH_COPYRIGHT' },
              { appName: APP_NAME_LONG, year: new Date().getFullYear() },
            )}
          </UtilityText>
        </StackLayout>
      </StackLayout>
    </StackLayout>
  );
};
